import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { ClockOutComment } from '../../scenes/time-tracking/ClockOutComment';
import { API_GET, API_POST, DBRequest, useConfig } from '../../api/api';
import Loader from '../special/Loader';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { ClockOutImage } from '../../scenes/time-tracking/ClockOutImage';
import { useUserPreferences } from '../../global/useUserPreferences';

const TimeProvider = createContext();

export const AppTimeTrackingProvider = ({ children, debug }) => {
  const config = useConfig();
  const { t } = useTranslation();

  const [activeProject, setActiveProject] = useState(null);
  const [secondsIn, setSecondsIn] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [daysToWork, setDaysToWork] = useState([]);
  const [minutesPerWeek, setMinutesPerWeek] = useState(0);
  const [payType, setPayType] = useState(0);
  const [vacation, setVacation] = useState({});

  // initial
  useEffect(() => {
    // get active project
    DBRequest({
      config,
      path: 'activeproject',
      method: API_GET,
      onResponse: handleActiveProject,
    });

    // get user Data
    DBRequest({
      config,
      path: 'user/timetracking',
      method: API_GET,
      onResponse: handleTimeTrackingUser,
    });
  }, [config]);

  function handleActiveProject(response) {
    setActiveProject(response.data);
  }

  // DASHBOARD
  const [minutesPerDay, setMinutesPerDay] = useState(null);
  const [timeAccountDB, setTimeAccountDB] = useState(null);
  const [minutesToday, setMinutesToday] = useState(null);
  const [openComment, setOpenComment] = useState(false);
  const [openPhoto, setOpenPhoto] = useState(false);
  const [pendingComment, setPendingComment] = useState(null);

  // store intended clock-in when switching projects/work packages
  const [pendingClockIn, setPendingClockIn] = useState(null);

  const { preferences } = useUserPreferences();
  const requirePhoto = Number(preferences?.requirePhotoClockOut);

  const handleTimeTrackingUser = useCallback((data) => {
    let newMinutesToday = 0;
    data.data.projectsToday.forEach((project) => {
      newMinutesToday += project.y;
    });
    setMinutesToday(newMinutesToday);
    setMinutesPerDay(data.data.minutesPerDay);
    setTimeAccountDB(data.data.timeAccount);
    setDaysToWork(data.data.daysInWeek);
    setMinutesPerWeek(data.data.minutesPerWeek);
    setPayType(data.data.payType);
    setVacation({
      total: data.data.totalVacationDays,
      taken: data.data.takenVacationDays,
    });
  }, []);

  const handleResetData = useCallback(() => {
    DBRequest({
      config,
      path: 'user/timetracking',
      method: API_GET,
      onResponse: handleTimeTrackingUser,
    });
    setSecondsIn(0);
    setActiveProject(null);
  }, [config, handleTimeTrackingUser]);

  // actual clock-in execution
  const performClockIn = useCallback(
    (project, workPackage) => {
      const data = {
        projectId: project.id,
        workPackageId: workPackage ? workPackage.id : null,
      };
      DBRequest({
        config,
        path: 'clockIn',
        method: API_POST(data),
        onError: handleResetData,
      });

      const clockInDateTime = moment();
      const formattedclockInDateTime = clockInDateTime.format();

      setActiveProject({
        ...project,
        activeWorkPackage: workPackage,
        clockInTime: formattedclockInDateTime,
      });
    },
    [config, handleResetData],
  );

  // clock in method with optional intermediate comment step if clocking out from another project is needed
  const clockIn = useCallback(
    (project, workPackage) => {
      // If already clocked in and comment is required on clock-out,
      // first ask for comment, then clock out, then clock in to new wp
      if (activeProject && Number(config.employee.requireCommentOnClockout)) {
        setPendingClockIn({ project, workPackage });
        setOpenComment(true);
        return;
      }

      // normal direct clock-in
      performClockIn(project, workPackage);
    },
    [activeProject, config, performClockIn],
  );

  // handles final clock-out request and optionally next clock-in
  const submitClockOut = useCallback(
    (comment, photoResource) => {
      const nextClockIn = pendingClockIn;

      DBRequest({
        config,
        path: 'clockOut',
        method: API_POST({
          comment,
          clockOutPhoto: photoResource ? photoResource?.data?.[0] : null,
        }),
        onResponse: () => {
          handleResetData();

          if (nextClockIn) {
            performClockIn(nextClockIn.project, nextClockIn.workPackage);
            setPendingClockIn(null);
          }
        },
        onLoading: setIsProcessing,
      });

      setPendingComment(null);
    },
    [config, handleResetData, pendingClockIn, performClockIn],
  );

  // After comment (or no comment needed) → photo dialog or direct clock-out
  const handleAfterComment = useCallback(
    (comment) => {
      setOpenComment(false);
      if (requirePhoto) {
        setPendingComment(comment);
        setOpenPhoto(true);
      } else {
        submitClockOut(comment, null);
      }
    },
    [requirePhoto, submitClockOut],
  );

  // After photo (or skip) → actual clock-out API call
  const handleAfterPhoto = useCallback(
    (photoResource) => {
      setOpenPhoto(false);
      submitClockOut(pendingComment, photoResource);
    },
    [pendingComment, submitClockOut],
  );

  // clock out
  const clockOut = useCallback(() => {
    setPendingClockIn(null); // normal clock-out, no follow-up clock-in

    if (Number(config.employee.requireCommentOnClockout)) {
      setOpenComment(true);
    } else {
      handleAfterComment('');
    }
  }, [config, handleAfterComment]);

  // CLOCK ----------------------
  useEffect(() => {
    if (!activeProject) return;

    setSecondsIn(updateTimeInSeconds(activeProject.clockInTime));

    let debugCounter = 0;
    const tickInterval = setInterval(() => {
      debugCounter += 60;
      if (activeProject) {
        const newSecondsIn = updateTimeInSeconds(
          activeProject.clockInTime,
          debugCounter,
        );
        setSecondsIn(newSecondsIn);
      }
    }, 1000);
    return () => clearInterval(tickInterval);
  }, [activeProject]);

  function updateTimeInSeconds(startDateTime, debugCounter) {
    const timeDiff = moment().diff(moment(startDateTime));
    const seconds = Math.round(timeDiff / 1000);
    if (debug) return debugCounter + seconds;
    return seconds;
  }

  // Derived values
  const currentMinutesToday = useMemo(
    () => minutesToday + Math.round(secondsIn / 60),
    [minutesToday, secondsIn],
  );

  const workCompletionPercentage = useMemo(
    () => (minutesPerDay > 0 ? currentMinutesToday / minutesPerDay : 1),
    [currentMinutesToday, minutesPerDay],
  );

  const timeAccount = useMemo(
    () => timeAccountDB + Math.round(secondsIn / 60),
    [timeAccountDB, secondsIn],
  );

  const contextValue = useMemo(
    () => ({
      activeProject,
      setActiveProject,
      clockOut,
      clockIn,
      daysToWork,
      secondsIn,
      minutesToday: currentMinutesToday,
      workCompletionPercentage,
      timeAccount,
      targetMinutes: minutesPerDay,
      minutesPerWeek,
      payType,
      vacation,
    }),
    [
      activeProject,
      clockOut,
      clockIn,
      daysToWork,
      secondsIn,
      currentMinutesToday,
      workCompletionPercentage,
      timeAccount,
      minutesPerDay,
      minutesPerWeek,
      payType,
      vacation,
    ],
  );

  return (
    <TimeProvider.Provider value={contextValue}>
      {children}
      <ClockOutComment
        open={openComment}
        onClose={() => setOpenComment(false)}
        onSubmit={handleAfterComment}
        requirePhoto={requirePhoto}
      />
      <ClockOutImage
        open={openPhoto}
        onClose={() => setOpenPhoto(false)}
        onSubmit={handleAfterPhoto}
        required={requirePhoto}
      />
      <Loader active={isProcessing} message={t('Processing') + '...'} />
    </TimeProvider.Provider>
  );
};

export const useTimeTracking = () => useContext(TimeProvider);
